import React, { useState, useEffect } from 'react';
import moment from 'moment';
import 'moment-timezone';
import DateUtil from '../../util/DateUtil';
import CommonUtils from '../../util/CommonUtils';
import { useSearchParams } from 'react-router-dom';
import SessionManager from '../../routes/SessionManager';
import API from '../../service/SiteKickAPIService';

const SiteMonitoringReport = () => {

    const [searchParams] = useSearchParams();

    const [user, setUser] = useState(null);
    const [siteLevel, setSiteLevel] = useState([]);
    // eslint-disable-next-line
    const [devices, setDevices] = useState([]);
    const [disconnectedGt, setDisconnectedGt] = useState([]);
    const [loading, setLoading] = useState(true);

    const [reportData, setReportData] = useState({
        projectManager: '',
        propertyName: '',
        weekEnding: '',
        startDate: '',
        endDate: '',
        location: '',
        totalCameras: 0,
        overallUptime: 0,
        totalEvents: 0,
        policeDispatches: 0,
        securityEvents: [],
        securitySummary: '',
        systemsMonitored: [],
        environmentalEvents: [],
        environmentalSummary: '',
        additionalNotes: '',
        preparedBy: '',
        preparedDate: '',
        contact: '',
        companyYear: new Date().getFullYear(),
        website: 'www.sitekick.com',
        supportEmail: 'support@sitekick.com',
        phone: '612-605-3170'
    });

    const [dates] = useState([
        moment().add(-7, 'days'),
        moment()
    ]);

    let timeZone = moment.tz.guess();

    // ----------------------------------------------------
    // STEP 1: INIT USER + SITE
    // ----------------------------------------------------
    useEffect(function () {
        document.body.style.background = 'white';
        async function init() {
            try {
                var params = {};
                searchParams.forEach(function (val, key) {
                    params[key] = val;
                });

                var userData = {
                    user_login: params.user_login,
                    user_token: params.user_token,
                    site_id: params.site_id,
                    customer_id: params.customer_id,
                    site_selected: { site_id: params.site_id }
                };

                let sessionManager = new SessionManager();
                sessionManager.storeCookie('user', userData);

                var site = await CommonUtils.getSite(userData.site_id, userData);
                var customer = await CommonUtils.getCustomer(site.customer_id, userData);
                userData.site_selected = site;
                userData.customer = customer;
                userData.customer_id = site.customer_id;

                timeZone = site.site_iana_timezone || moment.tz.guess();

                var locations = await CommonUtils.getSiteLocations(site.site_id, userData);
                userData.zones = locations.filter(function (z) {
                    return z.location_adminstatus === "Up";
                });



                setUser(userData);


            } catch (e) {
                console.error("INIT ERROR:", e);
                setLoading(false);
                // Signal ready even on error so Puppeteer doesn't wait indefinitely
                console.log('setting window.status to ready despite error  to prevent Puppeteer hang');
                window.status = 'ready';
            }
        }

        init();
    }, []);

    // ----------------------------------------------------
    // STEP 2: FETCH GATEWAY STATS WHEN USER LOADED
    // ----------------------------------------------------
    useEffect(function () {
        if (!user) return;
        fetchGatewayStats();
    }, [user]);

    // ----------------------------------------------------
    // STEP 3: After siteLevel -> fetch other data
    // ----------------------------------------------------
    useEffect(function () {
        if (!user) return;
        fetchDevices();
        fetchNotifications();
        fetchEnvironmentalData();
        fetchDisconnectedStats();
    }, [siteLevel]);

    // ----------------------------------------------------
    // STEP 4: After disconnected stats -> calc uptime
    // ----------------------------------------------------
    useEffect(function () {
        if (!user) return;
        computeOverallUptime();
    }, [disconnectedGt]);

    // ----------------------------------------------------
    // API: Gateway Stats
    // ----------------------------------------------------
    function fetchGatewayStats() {
        var start_ts = DateUtil.getUnixTimeStampInUTC(
            'America/Chicago',
            dates[0].format("YYYY-MM-DD")
        );
        var end_ts = DateUtil.getUnixTimeStampInUTC(
            'America/Chicago',
            dates[1].format("YYYY-MM-DD")
        );

        var data = {
            customer_id: user.site_selected.customer_id,
            start_ts: start_ts[0],
            end_ts: end_ts[1],
            user_login: user.user_login,
            user_token: user.user_token
        };

        CommonUtils.ajax({
            url: API.ADMIN.GATEWAY_STATS,
            type: 'POST',
            dataType: 'json',
            contentType: 'application/json',
            data: JSON.stringify(data)
        }).then(function (res) {

            if (res && res.data) {
                var site_level = res.data.filter(function (s) {
                    return s.site_id === user.site_selected.site_id;
                });
                setSiteLevel(site_level);

                setReportData(function (prev) {
                    return {
                        ...prev,
                        projectManager: user.user_name || "Project Manager",
                        propertyName: user.site_selected.site_name,
                        weekEnding: dates[1].format("MMMM D"),
                        startDate: dates[0].format("MM/DD/YYYY"),
                        endDate: dates[1].format("MM/DD/YYYY"),
                        totalCameras: user.zones.length,
                        preparedBy: "SiteKick Monitoring Team",
                        preparedDate: moment().format("MM/DD/YYYY"),
                        contact: "monitoring@sitekick.com | (555) 123-4567",
                        location:
                            user.site_selected.site_city +
                            ", " +
                            user.site_selected.site_state
                    };
                });
            } else {
                // No data from gateway stats — still trigger the chain so uptime runs
                setSiteLevel([]);
            }
        }).catch(function () {
            setSiteLevel([]);
        });
    }

    // ----------------------------------------------------
    // API: Disconnected Stats
    // ----------------------------------------------------
    function fetchDisconnectedStats() {
        var start_ts = DateUtil.getUnixTimeStampInUTC(
            'America/Chicago',
            dates[0].format("YYYY-MM-DD")
        );
        var end_ts = DateUtil.getUnixTimeStampInUTC(
            'America/Chicago',
            dates[1].format("YYYY-MM-DD")
        );

        var payload = {
            site_id: user.site_selected.site_id,
            start_ts: start_ts[0],
            end_ts: end_ts[1],
            user_login: user.user_login,
            user_token: user.user_token
        };

        CommonUtils.ajax({
            url: API.ADMIN.DISCONNECTED_BY_DATE,
            type: 'POST',
            dataType: 'json',
            contentType: 'application/json',
            data: JSON.stringify(payload)
        }).then(function (res) {
            if (res && res.data && Array.isArray(res.data[2])) {
                setDisconnectedGt(res.data[2]);
            } else {
                setDisconnectedGt([]);
            }
        }).catch(function () {
            setDisconnectedGt([]);
        });
    }

    // ----------------------------------------------------
    // API: Devices
    // ----------------------------------------------------
    function fetchDevices() {
        var payload = {
            user_login: user.user_login,
            user_token: user.user_token,
            form_input: {
                query: JSON.stringify({
                    attributes: [
                        'site_id',
                        'location_id',
                        'device_id',
                        'device_assetsn'
                    ],
                    object: 'device',
                    filters: [
                        {
                            attribute: 'site_id',
                            value: user.site_selected.site_id,
                            operator: '='
                        }
                    ],
                    sort_by: [{ attribute: 'location_id', value: 'asc' }],
                    pagination: { page: 1, size: 1000 }
                })
            }
        };

        CommonUtils.ajax({
            url: API.DATA.EXPLORER,
            type: 'POST',
            dataType: 'json',
            contentType: 'application/json',
            data: JSON.stringify(payload)
        }).then(function (res) {
            if (res && res.status && res.status.code === 200) {
                setDevices(res.data);
            }
        });
    }

    // ----------------------------------------------------
    // API: Security Events
    // ----------------------------------------------------
    function fetchNotifications() {
        var start_ts = DateUtil.getUnixTimeStampInUTC(
            'America/Chicago',
            dates[0].format("YYYY-MM-DD")
        );
        var end_ts = DateUtil.getUnixTimeStampInUTC(
            'America/Chicago',
            dates[1].format("YYYY-MM-DD")
        );

        var payload = {
            user_login: user.user_login,
            user_token: user.user_token,
            site_id: user.site_selected.site_id,
            form_input: {
                query: JSON.stringify({
                    attributes: ['*'],
                    object: 'event_history',
                    filters: [
                        { attribute: 'dev_timestamp', value: end_ts[1], operator: '<=' },
                        {
                            join_by: 'and',
                            attribute: 'dev_timestamp',
                            value: start_ts[0],
                            operator: '>='
                        }
                    ],
                    sort_by: [{ attribute: 'dev_timestamp', value: 'desc' }],
                    pagination: { page: 1, size: 1000 }
                })
            }
        };

        CommonUtils.ajax({
            url: API.DATA.EXPLORER,
            type: 'POST',
            dataType: 'json',
            contentType: 'application/json',
            data: JSON.stringify(payload)
        }).then(function (res) {
            if (!(res && res.status && res.status.code === 200)) return;

            var sorted = res.data.sort(function (a, b) {
                return b.dev_timestamp - a.dev_timestamp;
            });

            var eventMap = {};
            sorted.forEach(function (ev) {
                var key =
                    moment.unix(ev.dev_timestamp).format("MM/DD") +
                    "_" +
                    ev.event_type +
                    "_" +
                    ev.location_id;

                if (!eventMap[key]) {
                    eventMap[key] = ev;
                }
            });

            var evs = Object.values(eventMap).slice(0, 10).map(function (ev) {
                var zone = user.zones.find(function (z) {
                    return z.location_id === ev.location_id;
                });
                var loc = zone ? zone.location_name : "Unknown Zone";

                return {
                    dateTime: moment.tz(ev.dev_timestamp * 1000, timeZone).format("MM-DD-YYYY HH:mm"),
                    eventType: ev.event_type,
                    cameraZone: loc,
                    description: ev.event_message ? ev.event_message.split(',')[0] : "Event detected"
                };
            });

            setReportData(function (prev) {
                return {
                    ...prev,
                    totalEvents: res.data.length,
                    securityEvents: evs,
                    securitySummary:
                        "Total " +
                        (res.data.length).toLocaleString('en-US') +
                        " events recorded during this period."
                };
            });
        });
    }

    // ----------------------------------------------------
    // API: Environmental Data
    // ----------------------------------------------------
    function fetchEnvironmentalData() {
        var payload = {
            customer_id: user.customer_id,
            site_id: user.site_selected.site_id,
            user_login: user.user_login,
            user_token: user.user_token,
            start_date: dates[0].format("YYYY-MM-DD"),
            end_date: dates[1].format("YYYY-MM-DD"),
            timezone: timeZone
        };

        CommonUtils.ajax({
            url: API.DATA.ENVIRONMENTAL,
            type: 'POST',
            dataType: 'json',
            contentType: 'application/json',
            data: JSON.stringify(payload)
        }).then(function (res) {
            if (!(res && res.status && res.status.code === 200 && res.data)) {
                setReportData(function (prev) {
                    return {
                        ...prev,
                        environmentalSummary:
                            "No environmental monitoring data available."
                    };
                });
                return;
            }

            var valid = res.data.filter(function (e) {
                return e.event_value !== null && e.event_value !== -100;
            });

            var sorted = valid.sort(function (a, b) {
                return b.sec_timestamp - a.sec_timestamp;
            });

            var envMap = {};
            sorted.forEach(function (it) {
                var key =
                    moment
                        .tz(it.sec_timestamp * 1000, timeZone)
                        .format("YYYY-MM-DD") +
                    "_" +
                    it.sensor_id +
                    "_" +
                    it.location_id;

                if (!envMap[key]) {
                    envMap[key] = it;
                }
            });

            var events = Object.values(envMap)
                .slice(0, 10)
                .map(function (it) {
                    var zone = user.zones.find(function (z) {
                        return z.location_id === it.location_id;
                    });

                    var loc = zone ? zone.location_name : "Unknown Location";

                    var unitMap = {
                        temperature: "F",
                        humidity: "%",
                        co: "ppm",
                        co2: "ppm",
                        voc: "ppb",
                        leak: "",
                        flow: "Gallons"
                    };

                    console.log('timeZone', timeZone)

                    return {
                        dateTime: moment.tz(it.sec_timestamp * 1000, timeZone).format("MM-DD-YYYY HH:mm"),
                        sensorType: it.sensor_id.toUpperCase(),
                        location: loc,
                        event:
                            it.event_value +

                            (unitMap[it.sensor_id] || ""),
                        actionTaken: "Monitored"
                    };
                });

            var systems = [];
            res.data.forEach(function (e) {
                if (
                    e.sensor_id &&
                    systems.indexOf(e.sensor_id.charAt(0).toUpperCase() + e.sensor_id.slice(1)) === -1
                ) {
                    systems.push(
                        e.sensor_id.charAt(0).toUpperCase() + e.sensor_id.slice(1)
                    );
                }
            });

            setReportData(function (prev) {
                return {
                    ...prev,
                    systemsMonitored: systems,
                    environmentalEvents: events,
                    environmentalSummary:
                        "Total " +
                        (valid.length).toLocaleString('en-US') +
                        " environmental readings recorded."
                };
            });
        });
    }

    // ----------------------------------------------------
    //  Compute Overall Uptime (robust & clamped intervals)
    // ----------------------------------------------------
    function signalReady() {
        setLoading(false);
        setTimeout(() => {
            document.head.innerHTML = '';
            window.status = 'ready';
        }, 5000);
    }

    function computeOverallUptime() {
        if (!user || !user.zones || user.zones.length === 0) {
            signalReady();
            return;
        }

        var start_ts = DateUtil.getUnixTimeStampInUTC(
            'America/Chicago',
            dates[0].format("YYYY-MM-DD")
        );
        var end_ts = DateUtil.getUnixTimeStampInUTC(
            'America/Chicago',
            dates[1].format("YYYY-MM-DD")
        );

        var reportStart = start_ts[0];
        var reportEnd = end_ts[1];
        var totals = []; // will hold numeric uptimes (0-100) for active zones

        user.zones.forEach(function (zone) {
            // If zone explicitly unplugged, skip it from the average (treated as not-applicable)
            if (zone.location_unplugged) {
                return; // skip in averaging
            }

            var totalWindow = reportEnd - reportStart;
            if (totalWindow <= 0) {
                totals.push(0);
                return;
            }

            // All records for this device from the API (up to endtime)
            var recs = disconnectedGt.filter(function (d) {
                return d.device_id === zone.device_id;
            });

            if (recs.length === 0) {
                // No downtime records -> device was up the whole window
                totals.push(100);
                return;
            }

            var downTime = 0;
            recs.forEach(function (r) {
                var ds = typeof r.down_timestamp === 'number' ? r.down_timestamp : reportStart;
                var ue = typeof r.up_timestamp === 'number' ? r.up_timestamp : reportEnd; // null = still down

                // Clamp interval to report window
                var clampedStart = Math.max(ds, reportStart);
                var clampedEnd = Math.min(ue, reportEnd);
                if (clampedEnd > clampedStart) downTime += (clampedEnd - clampedStart);
            });

            if (downTime > totalWindow) downTime = totalWindow;

            let upPct = Math.max(0, ((totalWindow - downTime) / totalWindow) * 100);
            totals.push(upPct);
        });

        // Compute overall average across zones we've collected (exclude unplugged zones)
        var overall = 0;
        if (totals.length === 0) {
            // No active zones (all unplugged?) -> treat as 0 or NA; choose 0 here
            overall = 0;
        } else {
            overall = totals.reduce(function (a, b) { return a + b; }, 0) / totals.length;
        }

        setReportData(function (prev) {
            return {
                ...prev,
                overallUptime: parseFloat(overall.toFixed(2))
            };
        });

        signalReady();
    }

    // ----------------------------------------------------
    //  Zone Uptime (single zone, robust & clamped)
    // ----------------------------------------------------
    function calculateZoneUptime(zone) {
        if (!user || !user.zones) return "NA";

        var start_ts = DateUtil.getUnixTimeStampInUTC(
            'America/Chicago',
            dates[0].format("YYYY-MM-DD")
        );
        var end_ts = DateUtil.getUnixTimeStampInUTC(
            'America/Chicago',
            dates[1].format("YYYY-MM-DD")
        );

        var reportStart = start_ts[0];
        var reportEnd = end_ts[1];
        var totalWindow = reportEnd - reportStart;
        if (totalWindow <= 0) return "0.00";

        if (zone.location_unplugged) return "NA";

        // All records for this device from the API (up to endtime)
        var recs = disconnectedGt.filter(function (d) {
            return d.device_id === zone.device_id;
        });

        if (recs.length === 0) {
            // No downtime records -> device was up the whole window
            return "100.00";
        }

        var downTime = 0;
        recs.forEach(function (r) {
            var ds = typeof r.down_timestamp === 'number' ? r.down_timestamp : reportStart;
            var ue = typeof r.up_timestamp === 'number' ? r.up_timestamp : reportEnd; // null = still down

            // Clamp interval to report window
            var clampedStart = Math.max(ds, reportStart);
            var clampedEnd = Math.min(ue, reportEnd);
            if (clampedEnd > clampedStart) downTime += (clampedEnd - clampedStart);
        });

        if (downTime > totalWindow) downTime = totalWindow;

        let upPct = Math.max(0, ((totalWindow - downTime) / totalWindow) * 100);
        return upPct.toFixed(2);
    }


    // // ----------------------------------------------------
    // // GET SERIAL NO
    // // ----------------------------------------------------
    // function getSerialNo(zone) {
    //     var d = devices.find(function (dev) {
    //         return dev.location_id === zone.location_id;
    //     });

    //     if (d && d.device_assetsn) return d.device_assetsn;

    //     return zone.device_id === "0" ? zone.location_id : zone.device_id;
    // }

    // ----------------------------------------------------
    // RETURN UI
    // ----------------------------------------------------
    if (!user) {
        return <div>Loading user…</div>;
    }

    const sectionStyle = {
        padding: '30px',
        borderBottom: '1px solid #e8e8e8'
    };

    const h2Style = {
        fontWeight: 'bold',
        margin: '0 0 5px 0',
        fontSize: '14px',
        color: 'white',
        padding: '10px 5px',
        background: '#6b33b4'
    };

    // Stats layout using table for email compatibility
    const statsTableStyle = {
        width: '100%',
        marginBottom: '20px',
        borderCollapse: 'collapse'
    };

    const statBoxStyle = {
        backgroundColor: '#f9f9f9',
        borderLeft: '4px solid #6b33b4',
        padding: '20px',
        borderRadius: '4px'
    };

    const statLabelStyle = {
        fontSize: '12px',
        color: '#000000',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '8px'
    };

    const statValueStyle = {
        fontSize: '28px',
        fontWeight: 'bold',
        color: '#000000'
    };

    const tableStyle = {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px',
        borderRadius: '4px',
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px'
    };

    const theadStyle = {
        backgroundColor: '#deccf2',
        color: '#000000'
    };

    const thStyle = {
        padding: '12px',
        textAlign: 'left',
        fontWeight: 'normal'
    };

    const tdStyle = {
        color: '#000000',
        padding: '12px',
        borderBottom: '1px solid #e8e8e8'
    };

    const summaryBoxStyle = {
        backgroundColor: '#e6f7ff',
        borderLeft: '4px solid #1890ff',
        padding: '15px',
        borderRadius: '4px',
        marginTop: '20px',
        fontSize: '14px',
        lineHeight: '1.6'
    };

    // const notesBoxStyle = {
    //     backgroundColor: '#fffbe6',
    //     borderLeft: '4px solid #faad14',
    //     padding: '15px',
    //     borderRadius: '4px',
    //     fontSize: '14px',
    //     lineHeight: '1.6',
    //     color: '#000'
    // };

    const footerStyle = {
        padding: '30px',
        backgroundColor: '#fafafa',
        borderRadius: '8px'
    };

    // Email-compatible responsive CSS styles
    const responsiveStyles = `
        /* Reset and base styles */
        * {
            box-sizing: border-box;
        }

        .map-iframe .place-card{
            display: none !important;
        }

        /* Mobile responsive styles */
        @media only screen and (max-width: 600px) {
            .report-container {
                margin: 5px !important;
            }
            
            .report-section {
                padding: 15px !important;
            }
            
            .report-header {
                padding: 15px 10px !important;
            }
            
            .report-title {
                font-size: 14px !important;
            }
            
            .stat-cell {
                display: block !important;
                width: 100% !important;
                padding: 5px 0 !important;
            }
            
            .stat-box {
                padding: 12px !important;
            }
            
            .stat-label {
                font-size: 10px !important;
            }
            
            .stat-value {
                font-size: 20px !important;
            }
            
            .data-table {
                font-size: 11px !important;
            }
            
            .data-table th,
            .data-table td {
                padding: 8px 4px !important;
                word-break: break-word !important;
            }
            
            .camera-image {
                width: 60px !important;
                max-width: 60px !important;
            }
            
            .map-iframe {
                width: 100% !important;
                max-width: 100% !important;
                height: 200px !important;
            }
            
            .summary-box,
            .notes-box {
                padding: 10px !important;
                font-size: 12px !important;
            }
            
            .footer-section {
                padding: 15px !important;
            }
            
            .footer-text {
                font-size: 11px !important;
            }
            
            .property-label {
                width: auto !important;
                display: block !important;
                padding-bottom: 0 !important;
            }
            
            .property-value {
                display: block !important;
                padding-left: 0 !important;
            }
            
            /* Hide some columns on very small screens */
            .hide-mobile {
                display: none !important;
            }
        }
        
        /* Tablet responsive styles */
        @media only screen and (max-width: 768px) and (min-width: 601px) {
            .report-section {
                padding: 20px !important;
            }
            
            .stat-value {
                font-size: 24px !important;
            }
            
            .camera-image {
                width: 80px !important;
            }
            
            .map-iframe {
                width: 100% !important;
                max-width: 100% !important;
                height: 280px !important;
            }
                
        }
    `;

    return (
        <div>
            {/* Embedded responsive styles for email compatibility */}
            <style dangerouslySetInnerHTML={{ __html: responsiveStyles }} />

            {/* <div style={{ display: "none" }}>uid: ${Date.now()}</div> */}
            <div className="report-container" style={{
                borderRadius: '8px',
                margin: '10px',
                padding: '0',
                backgroundColor: '#f5f5f5',
                fontFamily: 'Arial, Helvetica, sans-serif',
                maxWidth: '100%'
            }}>
                <div style={{
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e0e0e0'
                }}>

                    {/* Header */}
                    <div className="report-header" style={{
                        backgroundColor: '#f4f4f4ff',
                        padding: '30px 20px',
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                    }}>
                        <span>logo</span>
                        <center><span className="report-title" style={{
                            fontSize: '18px',
                            fontWeight: 'bold',
                            color: '#6b33b4'
                        }}>SiteKick Jobsite Monitoring Report</span></center>
                    </div>

                    {/* Property Overview */}
                    <div className="report-section" style={sectionStyle}>
                        <h2 style={h2Style}>Property Overview</h2>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                                <tr>
                                    <td className="property-label" style={{ padding: '8px 0px', fontSize: '14px', width: '150px', color: '#000000', whiteSpace: 'nowrap' }}><strong>Customer Name:</strong></td>
                                    <td className="property-value" style={{ padding: '8px 0px', fontSize: '14px', paddingLeft: '2px', whiteSpace: 'nowrap' }}> {user.customer.customer_busname}</td>
                                </tr>
                                <tr>
                                    <td className="property-label" style={{ padding: '8px 0px', fontSize: '14px', width: '150px', color: '#000000', whiteSpace: 'nowrap' }}><strong>Property Name:</strong></td>
                                    <td className="property-value" style={{ padding: '8px 0px', fontSize: '14px', paddingLeft: '2px', whiteSpace: 'nowrap' }}> {user.site_selected.site_name}</td>
                                </tr>
                                <tr>
                                    <td className="property-label" style={{ padding: '8px 0px', fontSize: '14px', width: '150px', color: '#000000', whiteSpace: 'nowrap' }}><strong>Reporting Period:</strong></td>
                                    <td className="property-value" style={{ padding: '8px 0px', fontSize: '14px', paddingLeft: '2px', whiteSpace: 'nowrap' }}> {dates[0].format("MMMM DD, YYYY")} - {dates[1].format("MMMM DD, YYYY")}</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px 0', fontSize: '14px', width: '150px', color: '#000000' }} colSpan={2}>
                                        <iframe className="map-iframe" referrerPolicy="no-referrer-when-downgrade" title={user.site_selected.site_id} id="gmap_canvas" width="600" height="350" style={{ border: 0, maxWidth: '100%' }} src={["https://maps.google.com/maps?q=", user.site_selected.site_geolocation, "&t=&z=13&ie=UTF8&iwloc=&output=embed"].join('')} frameBorder="0" scrolling="no" marginHeight="0" marginWidth="0"></iframe>
                                        <span>map</span>
                                        <br />
                                        {user.site_selected.site_city}, {user.site_selected.site_stateprov}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Camera Overview */}
                    <div className="report-section" style={sectionStyle}>
                        <h2 style={h2Style}>Camera Overview</h2>

                        <table style={statsTableStyle}>
                            <tbody>
                                <tr>
                                    <td className="stat-cell" style={{ width: '50%', padding: '10px', verticalAlign: 'top' }}>
                                        <div className="stat-box" style={statBoxStyle}>
                                            <div className="stat-label" style={statLabelStyle}>Total Cameras Installed</div>
                                            <div className="stat-value" style={statValueStyle}>{user.zones.length}</div>
                                        </div>
                                    </td>
                                    <td className="stat-cell" style={{ width: '50%', padding: '10px', verticalAlign: 'top' }}>
                                        <div className="stat-box" style={statBoxStyle}>
                                            <div className="stat-label" style={statLabelStyle}>Overall Uptime</div>
                                            <div className="stat-value" style={{ ...statValueStyle, color: reportData.overallUptime >= 95 ? '#52c41a' : '#ff4d4f' }}>
                                                {parseInt(reportData.overallUptime)}%
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {loading ? (
                            <p>Loading camera data...</p>
                        ) : (

                            <table className="data-table" style={tableStyle}>
                                <thead style={theadStyle}>
                                    <tr>
                                        <th style={thStyle}>Camera</th>
                                        <th style={thStyle}>Latest Image</th>
                                        <th style={thStyle}>Uptime</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {user && user.zones && user.zones.length > 0 ? (
                                        user.zones.map((zone, index) => {
                                            const uptime = calculateZoneUptime(zone);
                                            const uptimeValue = uptime === 'NA' ? null : parseInt(uptime);
                                            const uptimeColor = uptime === 'NA' ? '#000000' : (uptimeValue >= 99 ? '#52c41a' : uptimeValue >= 95 ? '#faad14' : '#ff4d4f');

                                            return (
                                                <tr key={zone.location_id || index} style={index % 2 === 1 ? { backgroundColor: '#f5f5f5' } : { backgroundColor: '#ffffff' }}>
                                                    <td style={tdStyle}>{zone.location_name}</td>
                                                    <td style={tdStyle}><img className="camera-image" src={zone.image} width="100px" style={{ maxWidth: '100%', height: 'auto' }} alt={zone.location_name} /></td>
                                                    <td style={{ ...tdStyle, color: uptimeColor, fontWeight: 'bold' }}>
                                                        {uptime === 'NA' ? 'NA' : `${parseInt(uptime)}%`}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="3" style={{ ...tdStyle, textAlign: 'center', fontStyle: 'italic', color: '#000000' }}>
                                                No camera data available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}

                        <div className="summary-box" style={summaryBoxStyle}>
                            <label style={{ color: 'red' }}>Note:</label> The images displayed above are the latest snapshots from each camera and image links expire after 7 days.<br />
                            <strong style={{ color: '#1890ff', marginRight: '5px' }}><span role='img' aria-label='chart'>📊</span> Summary:</strong>
                            All cameras monitored during the reporting period. Overall system uptime: {reportData.overallUptime}%.
                        </div>
                    </div>

                    {/* Security Events */}
                    <div className="report-section" style={sectionStyle}>
                        <h2 style={h2Style}>Security Events</h2>

                        <table style={statsTableStyle}>
                            <tbody>
                                <tr>
                                    <td colSpan={2} className="stat-cell" style={{ width: '50%', padding: '10px', verticalAlign: 'top' }}>
                                        <div className="stat-box" style={statBoxStyle}>
                                            <div className="stat-label" style={statLabelStyle}>Total Events This Week</div>
                                            <div className="stat-value" style={statValueStyle}>{(reportData.totalEvents).toLocaleString('en-US')}</div>
                                        </div>
                                    </td>
                                    {/* <td className="stat-cell" style={{ width: '50%', padding: '10px', verticalAlign: 'top' }}>
                                        <div className="stat-box" style={statBoxStyle}>
                                            <div className="stat-label" style={statLabelStyle}>Police Dispatches</div>
                                            <div className="stat-value" style={statValueStyle}>{reportData.policeDispatches > 0 ? reportData.policeDispatches : 'None'}</div>
                                        </div>
                                    </td> */}
                                </tr>
                            </tbody>
                        </table>

                        <table className="data-table" style={tableStyle}>
                            <thead style={theadStyle}>
                                <tr>
                                    <th style={thStyle}>Date</th>
                                    <th style={thStyle}>Camera</th>
                                    <th style={thStyle}>Description</th>
                                    <th style={thStyle}>Response</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.securityEvents.length > 0 ? (
                                    reportData.securityEvents.filter(event => {
                                        return event.description.indexOf('weather_') === -1;
                                    }).map((event, index) => (
                                        <tr key={index} style={index % 2 === 1 ? { backgroundColor: '#f5f5f5' } : { backgroundColor: '#ffffff' }}>
                                            <td style={tdStyle}>{event.dateTime}</td>
                                            <td style={tdStyle}>{event.cameraZone}</td>
                                            <td style={{ ...tdStyle, textTransform: 'capitalize' }}>{event.description}</td>
                                            <td style={tdStyle}>{"Notification Sent"}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{ ...tdStyle, textAlign: 'center', fontStyle: 'italic', color: '#000000' }}>
                                            No security events recorded
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {reportData.securitySummary && (
                            <div style={summaryBoxStyle}>
                                <strong style={{ color: '#1890ff', marginRight: '5px' }}><span role='img' aria-label='chart'>📊</span> Summary:</strong>
                                {reportData.securitySummary}
                            </div>
                        )}
                    </div>

                    {/* Environmental Monitoring */}
                    <div className="report-section" style={sectionStyle}>
                        <h2 style={h2Style}>Environmental Events</h2>
                        <span style={{ fontSize: "14px" }}><strong>Systems Monitored:</strong> {reportData.systemsMonitored.join(' | ')}</span>

                        <table className="data-table" style={{ ...tableStyle, marginTop: '10px' }}>
                            <thead style={theadStyle}>
                                <tr>
                                    <th style={thStyle}>Date/Time</th>
                                    <th style={thStyle}>Sensor Type</th>
                                    <th style={thStyle}>Location</th>
                                    <th style={thStyle}>Event</th>
                                    <th style={thStyle}>Action Taken</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.environmentalEvents.length > 0 ? (
                                    reportData.environmentalEvents.map((event, index) => (
                                        <tr key={index} style={index % 2 === 1 ? { backgroundColor: '#f5f5f5' } : {}}>
                                            <td style={tdStyle}>{event.dateTime}</td>
                                            <td style={{ ...tdStyle, textTransform: 'capitalize' }}>{event.sensorType.toLowerCase()}</td>
                                            <td style={tdStyle}>{event.location}</td>
                                            <td style={tdStyle}>{event.event}</td>
                                            <td style={tdStyle}>{"Logged" || event.actionTaken}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{ ...tdStyle, textAlign: 'center', fontStyle: 'italic', color: '#000000' }}>
                                            No environmental events recorded
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {reportData.environmentalSummary && (
                            <div className="summary-box" style={summaryBoxStyle}>
                                <strong style={{ color: '#1890ff', marginRight: '5px' }}><span role='img' aria-label='chart'>📊</span> Summary:</strong>
                                {reportData.environmentalSummary}
                            </div>
                        )}
                    </div>

                    {/* Additional Notes */}
                    {/* <div className="report-section" style={sectionStyle}>
                        <h2 style={h2Style}>Additional Notes</h2>
                        <div className="notes-box" style={notesBoxStyle}>
                            {reportData.additionalNotes || 'No additional notes.'}
                        </div>
                    </div> */}

                    {/* Footer */}
                    <div className="footer-section" style={footerStyle}>
                        <div>
                            <p className="footer-text" style={{ margin: '5px 0', fontSize: '14px', color: '#000000' }}><strong>Prepared By:</strong> SiteKick Technologies, Inc.</p>
                            <p className="footer-text" style={{ margin: '5px 0', fontSize: '14px', color: '#000000' }}><strong>Date:</strong> {moment().format('YYYY-MM-DD HH:mm:ss')}</p>
                            <p className="footer-text" style={{ margin: '5px 0', fontSize: '14px', color: '#000000' }}><strong>Contact:</strong> {reportData.supportEmail}</p>
                        </div>
                        <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid #ddd' }} />
                        <div style={{ textAlign: 'center' }}>
                            <p className="footer-text" style={{ margin: '5px 0', color: '#000000', fontSize: '12px' }}>© {reportData.companyYear} SiteKick – Reality Capture & Monitoring Solutions</p>
                            <p className="footer-text" style={{ margin: '5px 0', color: '#000000', fontSize: '12px' }}>{reportData.website} | {reportData.supportEmail} | {reportData.phone}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SiteMonitoringReport;
